import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StageScreen, StagePopup, popupHasContent } from './components/StageShell';
import { s3PublicUrl } from '../../utils/s3PublicUrl';

const AUTO_ADVANCE_DELAY_MS = 900;

// The pool must not start in the same order as the numbered rank slots below
// it — that would line item 1 up under slot 1, item 2 under slot 2, and so
// on, letting the ranking be "solved" by position alone instead of by
// picking the actual country/flag.
function shuffleIds(ids) {
  const a = [...ids];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function Stage4Maps({ stage, pagesBg, onComplete }) {
  // Admin has no min_maps/max_maps fields — those are always undefined, so
  // falling back to a fixed 10 here silently truncated every stage to the
  // first 10 maps regardless of how many (10-20, per admin's own limits)
  // were actually entered. Fall back to the real uploaded count instead.
  const uploadedCount = Array.isArray(stage?.maps) ? stage.maps.length : 0;
  const minMaps = Math.max(1, Number(stage?.min_maps) || 5);
  const maxMaps = Math.min(20, Math.max(minMaps, Number(stage?.max_maps) || uploadedCount || 10));

  const items = useMemo(
    () =>
      (Array.isArray(stage?.maps) ? stage.maps : [])
        .slice(0, maxMaps)
        .map((m, i) => ({ ...m, id: i, text: m.text || `Item ${i + 1}` })),
    [stage?.maps, maxMaps]
  );
  const slotCount = Math.min(maxMaps, Math.max(minMaps, items.length));

  const startHasContent = popupHasContent(stage?.start_popup_image, stage?.start_popup_text);
  const endHasContent = popupHasContent(stage?.end_popup_image, stage?.end_popup_text);
  const [phase, setPhase] = useState(startHasContent ? 'start' : 'game');
  const [slots, setSlots] = useState(() => Array(slotCount).fill(null));
  const [pool, setPool] = useState(() => shuffleIds(items.map((it) => it.id)));
  const [pickedId, setPickedId] = useState(null);

  // Touch drag state
  const [touchDragId, setTouchDragId] = useState(null);
  const ghostRef = useRef(null);
  const touchDragIdRef = useRef(null);
  const hoverSlotIdxRef = useRef(null);
  // Keep a stable ref to items so the touch-end closure can look up the item
  const itemsRef = useRef(items);
  useEffect(() => { itemsRef.current = items; }, [items]);

  const itemById = (id) => items.find((it) => it.id === id) || null;

  const placeInSlot = useCallback((slotIdx, id) => {
    if (id == null) return;
    setSlots((prev) => {
      const existingAtTarget = prev[slotIdx];
      if (existingAtTarget === id) return prev;
      const next = [...prev];
      // If the flag is being dragged in from another slot (not the pool), clear
      // that slot too — otherwise it stays placed there AND in the new slot.
      const sourceIdx = next.findIndex((v) => v === id);
      if (sourceIdx !== -1) {
        next[sourceIdx] = existingAtTarget; // swap the two slots' contents
      } else if (existingAtTarget != null) {
        setPool((p) => (p.includes(existingAtTarget) ? p : [...p, existingAtTarget]));
      }
      next[slotIdx] = id;
      return next;
    });
    setPool((p) => p.filter((x) => x !== id));
    setPickedId(null);
  }, []);

  const removeFromSlot = useCallback((slotIdx) => {
    setSlots((prev) => {
      const id = prev[slotIdx];
      if (id == null) return prev;
      const next = [...prev];
      next[slotIdx] = null;
      setPool((p) => (p.includes(id) ? p : [...p, id]));
      return next;
    });
  }, []);

  const filled = slots.filter((s) => s != null).length;

  const finishStage = () => {
    if (endHasContent) {
      setPhase('end');
    } else {
      onComplete({ ranking: slots.map((id) => (id != null ? itemById(id) : null)) });
    }
  };

  // Auto-advance once every slot is filled
  useEffect(() => {
    if (phase === 'game' && slotCount > 0 && filled >= slotCount) {
      const t = setTimeout(finishStage, AUTO_ADVANCE_DELAY_MS);
      return () => clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, filled, slotCount]);

  // ── Touch drag ──────────────────────────────────────────────────────────────

  const startTouchDrag = useCallback((id, clientX, clientY) => {
    touchDragIdRef.current = id;
    setTouchDragId(id);

    const item = itemsRef.current.find((it) => it.id === id);
    const ghost = document.createElement('div');
    ghost.className = 'fi-drag-ghost';
    ghost.style.left = clientX + 'px';
    ghost.style.top = clientY + 'px';

    if (item?.image) {
      const img = document.createElement('img');
      img.src = s3PublicUrl(item.image);
      ghost.appendChild(img);
    } else if (item?.text) {
      const span = document.createElement('span');
      span.textContent = item.text;
      ghost.appendChild(span);
    }

    document.body.appendChild(ghost);
    ghostRef.current = ghost;
  }, []);

  const onPoolTouchStart = useCallback((id, e) => {
    e.preventDefault();
    startTouchDrag(id, e.touches[0].clientX, e.touches[0].clientY);
  }, [startTouchDrag]);

  const onPlacedTouchStart = useCallback((id, slotIdx, e) => {
    e.preventDefault();
    removeFromSlot(slotIdx);
    startTouchDrag(id, e.touches[0].clientX, e.touches[0].clientY);
  }, [removeFromSlot, startTouchDrag]);

  // Attach global touchmove / touchend while a drag is in progress
  useEffect(() => {
    if (touchDragId == null) return;

    // Scrolling lives on `.fi-game-shell` (position:fixed + overflow-y:auto),
    // not window/body. During a drag we preventDefault so the gesture stays
    // with the ghost, and edge-auto-scroll that shell so top slots stay reachable.
    const shell = document.querySelector('.fi-game-shell');
    const EDGE_PX = 80;
    const SCROLL_STEP = 18;
    let autoScrollDir = 0; // -1 up, 1 down, 0 none
    let rafId = 0;

    const clearHover = () => {
      if (hoverSlotIdxRef.current != null) {
        const el = document.querySelector(`[data-slot-idx="${hoverSlotIdxRef.current}"]`);
        el?.classList.remove('fi-rank-slot--touch-over');
        hoverSlotIdxRef.current = null;
      }
    };

    const tickAutoScroll = () => {
      if (autoScrollDir !== 0 && shell) {
        shell.scrollTop += autoScrollDir * SCROLL_STEP;
      }
      rafId = requestAnimationFrame(tickAutoScroll);
    };
    rafId = requestAnimationFrame(tickAutoScroll);

    const onMove = (e) => {
      e.preventDefault();
      const touch = e.touches[0];

      if (ghostRef.current) {
        ghostRef.current.style.left = touch.clientX + 'px';
        ghostRef.current.style.top = touch.clientY + 'px';
        ghostRef.current.style.visibility = 'hidden';
      }
      const el = document.elementFromPoint(touch.clientX, touch.clientY);
      if (ghostRef.current) ghostRef.current.style.visibility = '';

      // Edge auto-scroll so the finger can reach slots above/below the fold
      if (touch.clientY < EDGE_PX) autoScrollDir = -1;
      else if (touch.clientY > window.innerHeight - EDGE_PX) autoScrollDir = 1;
      else autoScrollDir = 0;

      const slotEl = el?.closest('[data-slot-idx]');
      const newIdx = slotEl ? Number(slotEl.dataset.slotIdx) : null;
      const validIdx = newIdx != null && !Number.isNaN(newIdx) ? newIdx : null;

      if (validIdx !== hoverSlotIdxRef.current) {
        clearHover();
        if (validIdx != null && slotEl) {
          slotEl.classList.add('fi-rank-slot--touch-over');
          hoverSlotIdxRef.current = validIdx;
        }
      }
    };

    const onEnd = (e) => {
      // Without this, the browser still fires its normal ~300ms delayed
      // "phantom click" at wherever the finger lifted — landing right on
      // the slot the flag was just dropped into. That click hits the
      // slot's onClick (pickedId is already null by then), which treats it
      // as "tap an occupied slot to remove its flag" and immediately pulls
      // the just-placed flag back out. Looked like the drop "didn't stick"
      // or a previously-placed flag getting bumped by the next drag.
      e.preventDefault();
      const touch = e.changedTouches[0];
      const id = touchDragIdRef.current;
      autoScrollDir = 0;

      if (ghostRef.current) { ghostRef.current.remove(); ghostRef.current = null; }
      clearHover();

      const el = document.elementFromPoint(touch.clientX, touch.clientY);
      const slotEl = el?.closest('[data-slot-idx]');
      const idx = slotEl ? Number(slotEl.dataset.slotIdx) : null;

      if (idx != null && !Number.isNaN(idx) && id != null) {
        placeInSlot(idx, id);
      }

      touchDragIdRef.current = null;
      setTouchDragId(null);
    };

    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd, { passive: false });
    document.addEventListener('touchcancel', onEnd, { passive: false });

    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onEnd);
      if (ghostRef.current) { ghostRef.current.remove(); ghostRef.current = null; }
      clearHover();
    };
  }, [touchDragId, placeInSlot]);

  // ── Render ──────────────────────────────────────────────────────────────────

  if (phase === 'start') {
    return (
      <StageScreen pagesBg={pagesBg} pageKey="stage_4" stage={stage}>
        <StagePopup image={stage?.start_popup_image} text={stage?.start_popup_text} onNext={() => setPhase('game')} />
      </StageScreen>
    );
  }

  if (phase === 'end') {
    const ranking = slots.map((id) => (id != null ? itemById(id) : null));
    return (
      <StageScreen pagesBg={pagesBg} pageKey="stage_4" stage={stage}>
        <StagePopup image={stage?.end_popup_image} text={stage?.end_popup_text} onNext={() => onComplete({ ranking })} />
      </StageScreen>
    );
  }

  // The flag images already have the country name baked into the graphic —
  // showing a separate text label duplicated it (grayed baked-in name, then
  // a second bold name underneath). Image-only, text only as a fallback when
  // a map entry has no image configured.
  const renderFlag = (item) =>
    item.image ? <img src={s3PublicUrl(item.image)} alt={item.text} /> : <span className="fi-flag-item-label">{item.text}</span>;

  return (
    <StageScreen pagesBg={pagesBg} pageKey="stage_4" stage={stage}>
      <div className="fi-stage-root fi-s4-root">
        <h2 className="fi-heading">{stage?.heading}</h2>
        {stage?.subheading ? <p className="fi-stage-subheading">{stage.subheading}</p> : null}

        <div className="fi-rank-slots">
          {slots.map((id, i) => {
            const item = id != null ? itemById(id) : null;
            return (
              <div
                key={i}
                data-slot-idx={i}
                className={`fi-rank-slot ${pickedId != null ? 'fi-rank-slot--active' : ''}`}
                onClick={() => (pickedId != null ? placeInSlot(i, pickedId) : item ? removeFromSlot(i) : null)}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                onDrop={(e) => {
                  e.preventDefault();
                  const id2 = Number(e.dataTransfer.getData('text/plain'));
                  if (!Number.isNaN(id2)) placeInSlot(i, id2);
                }}
                role="button"
                tabIndex={0}
              >
                <span className="fi-rank-num">{i + 1}</span>
                {item ? (
                  <div
                    className="fi-flag-item fi-flag-item--placed"
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData('text/plain', String(item.id))}
                    onClick={(e) => { e.stopPropagation(); removeFromSlot(i); }}
                    onTouchStart={(e) => onPlacedTouchStart(item.id, i, e)}
                    title="Tap to remove"
                  >
                    {renderFlag(item)}
                  </div>
                ) : (
                  <span className="fi-rank-empty">drop here</span>
                )}
              </div>
            );
          })}
        </div>

        <p className="fi-stage1-progress">{filled} / {slotCount} ranked</p>

        <div className="fi-flags-pool">
          {pool.map((id) => {
            const item = itemById(id);
            if (!item) return null;
            const isPicked = pickedId === id;
            return (
              <button
                key={id}
                type="button"
                className={`fi-flag-item ${isPicked ? 'fi-flag-item--picked' : ''}`}
                draggable
                onDragStart={(e) => e.dataTransfer.setData('text/plain', String(id))}
                onClick={() => setPickedId(isPicked ? null : id)}
                onTouchStart={(e) => onPoolTouchStart(id, e)}
              >
                {renderFlag(item)}
              </button>
            );
          })}
        </div>
      </div>
    </StageScreen>
  );
}
